import { log } from '@/log'
import { CartScope } from '@/models/cart/group/scope'
import { BrandService, ExistByEnglishNameService, FindCartScopeService } from '@/services/api/brand'
import { MaybeFail, MaybeOtherScopeAvailable, RedirectConfig } from '@spa/router/hooks/cart/scope/redirect-config'
import { Either } from 'fp-ts/lib/Either'
import { NavigationGuard } from 'vue-router'

export type BrandResourceGateway = ExistByEnglishNameService & FindCartScopeService

export interface ScopeStateForBrandGateway {
  fetch(brand: string): Promise<{ availableScope: CartScope, brandIsAvailable: boolean }>
}
export class DefaultScopeStateForBrandGateway implements ScopeStateForBrandGateway {
  constructor(private readonly brandService: BrandResourceGateway) {}

  async fetch(brand: string): Promise<{ availableScope: CartScope; brandIsAvailable: boolean }> {
    const handle = <T>(r: Either<Error, T>) => r.fold(
      (e) => { throw e },
      v => v
    )
    const [availableScope, brandIsAvailable] = await Promise.all([
      this.brandService.findCartScope(brand).then(handle),
      this.brandService.existByEnglishName(brand).then(handle),
    ])

    return { availableScope, brandIsAvailable }
  }
}

interface GatewayConfig {
  brandResource: BrandResourceGateway
}

export const guardOnBrandScopeBuilder = (gatewayConfig: GatewayConfig) =>
  (config: RedirectConfig<MaybeOtherScopeAvailable & MaybeFail>): NavigationGuard =>
  async (to, _from, next): Promise<void> => {
    const brandScope = config.extractScope(to)
    if (!brandScope.kind.isBrand) {
      log.error(`guard on brand scope accept only brand scope. actual is ${brandScope.toString()}`)

      return next(config.redirect.whenErrorOccurred)
    }

    try {
      // gatewayのインスタンスを交換する必要が生じたら、
      // GatewayConfigに createGateway: GatewayConfig => ScopeStateForBrandGateway
      // のようなオプションを追加して、生成方法を選択可能にする
      const gateway = new DefaultScopeStateForBrandGateway(gatewayConfig.brandResource)
      const state = await gateway.fetch(brandScope.value.toString())

      if (!state.availableScope.equals(brandScope)) {
        return next(
          config.redirect.whenOtherScopeAvailable(state.availableScope)
        )
      }

      if (!state.brandIsAvailable) {
        return next(config.redirect.whenErrorOccurred)
      }

      return next()
    } catch (error) {
      log.error({ error })

      return next(config.redirect.whenErrorOccurred)
    }
  }
export const guardOnBrandScope = guardOnBrandScopeBuilder({
  brandResource: new BrandService(),
})
